import { Skeleton, SkeletonAvatar, SkeletonText } from '@/components/ui/skeleton';
import { KANBAN_COLUMN_WIDTH } from '@/components/shared/kanban';
import { cn } from '@/lib/utils';

/** Body-line counts, so comments don't all look the same length. */
const COMMENT_LINES = [2, 1, 3, 1] as const;
const AUTHOR_WIDTHS = ['96px', '128px', '104px', '116px'] as const;

interface CommentThreadSkeletonProps {
    readonly comments?: number;
    readonly className?: string;
}

/**
 * Loading placeholder for a comment thread: avatar, author, timestamp, body.
 *
 * Varying the body-line count per comment matters more here than anywhere else
 * — a thread of identically-sized blocks is the clearest possible tell that a
 * loading state was drawn rather than derived.
 */
export function CommentThreadSkeleton({ comments = 3, className }: CommentThreadSkeletonProps) {
    return (
        <div className={cn('flex flex-col gap-4', className)}>
            {Array.from({ length: comments }).map((_, i) => (
                <div key={i} className="flex gap-3">
                    <SkeletonAvatar size={28} className="mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <SkeletonText
                                size="sm"
                                width={AUTHOR_WIDTHS[i % AUTHOR_WIDTHS.length]}
                            />
                            <SkeletonText size="xs" width="72px" />
                        </div>
                        {/* `?? 2` satisfies noUncheckedIndexedAccess — the modulo
                            guarantees a hit, but the compiler can't know that. */}
                        {Array.from({ length: COMMENT_LINES[i % COMMENT_LINES.length] ?? 2 }).map(
                            (_line, j) => (
                                <SkeletonText
                                    key={j}
                                    size="sm"
                                    width={j === 0 ? '92%' : j === 1 ? '78%' : '54%'}
                                />
                            ),
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

interface ActivityFeedSkeletonProps {
    readonly items?: number;
    /** Timeline feeds draw a connecting rail between the dots. */
    readonly showRail?: boolean;
    readonly className?: string;
}

const ACTIVITY_WIDTHS = ['76%', '58%', '84%', '64%', '70%', '48%'] as const;

/**
 * Loading placeholder for an activity / audit feed: a dot or icon per event,
 * one line of description, and a relative timestamp.
 */
export function ActivityFeedSkeleton({
    items = 5,
    showRail = false,
    className,
}: ActivityFeedSkeletonProps) {
    return (
        <div className={cn('flex flex-col', className)}>
            {Array.from({ length: items }).map((_, i) => (
                <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                        <Skeleton className="mt-1.5 size-6 shrink-0 rounded-full" />
                        {showRail && i < items - 1 && (
                            <span className="w-px flex-1 bg-border-default" aria-hidden="true" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1 pb-4">
                        <SkeletonText
                            size="sm"
                            width={ACTIVITY_WIDTHS[i % ACTIVITY_WIDTHS.length]}
                        />
                        <SkeletonText size="xs" width="88px" />
                    </div>
                </div>
            ))}
        </div>
    );
}

interface BoardColumnsSkeletonProps {
    /** One column per board status. */
    readonly columns?: number;
    readonly cardsPerColumn?: number;
    readonly className?: string;
}

/**
 * Loading placeholder for a kanban board.
 *
 * Draws real card shapes — title, meta row, assignee avatar — rather than plain
 * blocks, so the column reads as "tickets are arriving here". The column shell
 * mirrors `KanbanBoard`'s exactly (rail, header band, rounded-xl surface) so the
 * board does not visibly jump when the real data lands.
 */
export function BoardColumnsSkeleton({
    columns = 4,
    cardsPerColumn = 2,
    className,
}: BoardColumnsSkeletonProps) {
    return (
        <div className={cn('flex h-full items-start gap-4 overflow-x-auto p-4', className)}>
            {Array.from({ length: columns }).map((_, col) => (
                <div
                    key={col}
                    className={cn(
                        KANBAN_COLUMN_WIDTH,
                        'flex max-h-full shrink-0 flex-col overflow-hidden rounded-xl border border-border-default bg-bg-surface',
                    )}
                >
                    <div className="flex shrink-0 items-center gap-2 px-3 py-2.5">
                        <div className="flex flex-col gap-1">
                            <SkeletonText size="sm" width="88px" />
                            <SkeletonText size="xs" width="52px" />
                        </div>
                        <Skeleton className="ml-auto h-5 w-8 rounded-full" />
                    </div>
                    <div className="flex min-h-24 flex-1 flex-col gap-2 p-2">
                        {Array.from({ length: cardsPerColumn }).map((_card, i) => (
                            <div
                                key={i}
                                className="flex shrink-0 flex-col gap-2 rounded-lg border border-border-default bg-bg-surface p-3"
                            >
                                <SkeletonText size="sm" width={i % 2 === 0 ? '82%' : '64%'} />
                                <div className="flex items-center justify-between gap-2">
                                    <Skeleton className="h-[22px] w-16 rounded-full" />
                                    <SkeletonAvatar size={24} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
